import { createContext, useContext } from 'react';

const DEFAULT_LABELS = {
  // Digest module key — must match one of the keys in digest_service.MODULE_LABELS on the worker.
  // Each layout overrides this with its own value; 'gemini' is the Gemini Enterprise default.
  module: 'gemini',
  productName: 'Gemini Enterprise',
  // Singular/plural noun the platform uses for the executable unit. Gemini
  // Enterprise inventories mix apps and agents, so the shared UI noun is
  // "Entity". M365 Copilot calls them "Apps", Copilot Studio calls them
  // "Agents". Surfaced through the shared conversations / users / posture
  // components so we don't fork the markup.
  noun: 'Entity',
  nounPlural: 'Entities',
  pageTitle: 'Entity Conversations',
  pageSubtitle: 'Explore sessions and conversations with Enterprise AI entities',
  dashboardSubtitle: 'Business intelligence, agent performance, and security insights for Gemini Enterprise Agents',
  usersStatHelp: 'Total number of unique users who have interacted with Gemini Enterprise Agents.',
  userSegmentsHelp: 'Users grouped by engagement level. Power Users are high-volume champions; Churn risk users show low usage or poor outcomes.',
  interactionEventType: 'gemini_interaction',
  totalStatHelp: 'Total number of conversation sessions across all Enterprise AI entities',
  totalStatTrend: 'All entities combined',
  unknownLabel: 'Unknown',
  responseSenderFallback: 'Entity',
  // Whether the platform exposes a multi-agent concept the user can filter
  // by. Gemini Enterprise has many distinct agents → true. M365 Copilot
  // has only "Microsoft 365 Chat" plus connectors that arrive on the
  // sender side → no useful agent filter, set false to hide it from the
  // top-of-page filter bars.
  hasAgentFilter: true,
  // Whether this platform's logs carry conversation content (prompt/response)
  // that enrichment (threats, PII, intents, knowledge gaps, semantic/session
  // intelligence) can be derived from. Gemini Enterprise + M365 Copilot send
  // content → true. Google Workspace Gemini's Reports API sends activity
  // metadata only (no prompt/response) → set false to hide content-only
  // dashboard sections that would otherwise render permanently empty.
  hasContent: true,
  // Per-platform labels. The license-cost field on the Settings page
  // refers to the platform's own license SKU — different per platform.
  licenseCostLabel: 'Google License Cost ($/user/month)',
  licenseCostHelp: 'Monthly cost per Gemini Enterprise license from Google. Used to calculate potential savings from unused licenses and cost per query.',
  licenseCostSubtitle: 'Per-user monthly Google Gemini Enterprise license cost',
  settingsPageTitle: 'Enterprise Agents Settings',
  settingsPageSubtitle: 'Configure ROI calculations and compliance frameworks for Gemini Enterprise Agents. Alerts, notifications, and digests are managed under Notifications and Alert Configuration.',
  // Base route prefix for this module — used by shared components (dashboard,
  // OnboardingChecklist) to navigate to the right module's sub-pages instead
  // of hardcoding /gemini/agents/... everywhere.
  routeBase: '/gemini/agents',
  // Optional, read by the shared compliance page and its detail panel; each
  // falls back to the interaction wording when unset (so every module that
  // does not set them reads exactly as before). Code Security sets them:
  //   hasUserFilter           false hides the user filter (no person in the evidence)
  //   evaluatedNoun / evaluatedNounPlural   what is evaluated ('interaction')
  //   complianceTitle / complianceSubtitle  the page heading
  //   complianceFrameworksTitle / complianceFrameworksHelp  the frameworks card
  //   complianceControlsHelp / complianceViolationsHelp / complianceEvaluatedHelp  tile tooltips
  //   complianceNoEvidenceText  a control's empty evidence list
  // AI Providers sets these two on the detail panel's evidence rows:
  //   complianceEvidenceUser  true shows each evidence record's user (log.user)
  //   complianceEvidencePreviewLabel  renames the "Detections" field when the
  //                                   preview is the prompt itself ('Prompt')
};

const AgentLabelsContext = createContext(DEFAULT_LABELS);

export const AgentLabelsProvider = AgentLabelsContext.Provider;

export function useAgentLabels() {
  return useContext(AgentLabelsContext);
}

export { DEFAULT_LABELS };
export default AgentLabelsContext;
